import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';

interface RevealProps {
  message: string;
  className?: string;
  onComplete: () => void;
  skipped?: boolean;
}

/* =========================================================================
   1. HANDWRITTEN JOURNAL — Live character-by-character writing
   Uses requestAnimationFrame-driven timeline, NOT setTimeout chains.
   Unique: blinking pen cursor, variable rhythm, per-char reveal.
   NO other reveal mode uses character-by-character writing.
   ========================================================================= */
export function HandwrittenReveal({ message, onComplete, skipped }: RevealProps) {
  const [charIndex, setCharIndex] = useState(0);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const delayRef = useRef<number>(0);
  const doneRef = useRef(false);
  const indexRef = useRef(0);

  useEffect(() => {
    if (skipped) { onComplete(); return; }

    indexRef.current = 0;
    doneRef.current = false;
    lastTimeRef.current = 0;
    delayRef.current = 700; // initial pause before writing starts

    const getCharDelay = (char: string): number => {
      const base = 38 + (Math.random() - 0.4) * 12; // 30-46ms range
      if (char === '.' || char === '!' || char === '?') return base + 220;
      if (char === ',') return base + 120;
      if (char === '\n') return base + 300;
      if (char === ';' || char === ':') return base + 100;
      return base;
    };

    const tick = (timestamp: number) => {
      if (doneRef.current) return;

      if (lastTimeRef.current === 0) {
        lastTimeRef.current = timestamp;
      }

      const elapsed = timestamp - lastTimeRef.current;

      if (elapsed >= delayRef.current) {
        lastTimeRef.current = timestamp;
        indexRef.current++;
        setCharIndex(indexRef.current);

        if (indexRef.current >= message.length) {
          doneRef.current = true;
          setTimeout(onComplete, 800);
          return;
        }

        delayRef.current = getCharDelay(message[indexRef.current - 1]);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [message, onComplete, skipped]);

  if (skipped) {
    return (
      <p className="font-handwriting text-xl md:text-2xl leading-loose whitespace-pre-wrap text-foreground">
        {message}
      </p>
    );
  }

  return (
    <div className="relative">
      {/* Warm vignette lighting */}
      <div
        className="absolute inset-0 pointer-events-none rounded-lg"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, transparent 50%, rgba(139,90,43,0.06) 100%)',
        }}
      />

      {/* Floating dust particles */}
      {Array.from({ length: 6 }).map((_, i) => (
        <motion.div
          key={`dust-${i}`}
          className="absolute rounded-full pointer-events-none"
          style={{
            width: 2,
            height: 2,
            left: `${15 + i * 14}%`,
            top: `${20 + (i * 17) % 60}%`,
            background: `radial-gradient(circle, rgba(180,150,100,${0.15 + (i % 3) * 0.08}), transparent)`,
          }}
          animate={{
            y: [0, -(8 + i * 3), 0],
            opacity: [0, 0.4, 0],
          }}
          transition={{
            duration: 4 + i * 0.6,
            delay: 2 + i * 0.8,
            repeat: Infinity,
            ease: [0.22, 0.61, 0.36, 1],
          }}
        />
      ))}

      <p className="font-handwriting text-xl md:text-2xl leading-loose whitespace-pre-wrap text-foreground relative z-10">
        {message.slice(0, charIndex)}

        {/* Ink bloom on sentence-ending punctuation */}
        {charIndex > 0 && ['.', '!', '?'].includes(message[charIndex - 1]) && (
          <motion.span
            className="inline-block w-1 h-1 rounded-full bg-primary/30 ml-0.5 align-middle"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 3, 0], opacity: [0, 0.4, 0] }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        )}

        {charIndex < message.length && (
          <motion.span
            animate={{ y: [0, -2, 0], rotate: [0, -8, 0] }}
            transition={{ duration: 0.6, repeat: Infinity, ease: 'easeInOut' }}
            className="inline-flex items-center ml-0.5 align-middle text-primary"
          >
            <PenLine className="h-5 w-5" />
          </motion.span>
        )}
      </p>

      {/* Subtle camera drift via container motion */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{ x: [0, 1, -1, 0], y: [0, -0.5, 0.5, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
}
